import * as React from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import _ from "lodash";
import { formatCurrency, formatDate } from "../../helpers/UtilityHelper";
import { Panel, Lollipop, Donut, StackedComposition, AreaCurve } from "../dashboard/NewCharts";
import DelayMisDownloadButton from "./DelayMisDownloadButton";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import OrderItemsModal from "../../common/OrderItemsModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { apiConstants, post } from "../../common/constants";
import { FONT, WEIGHT } from "../../styles/foundation/typography";
import { BORDER, SUBTLE, WHITE, ERROR, SUCCESS } from "../../styles/foundation/colors";
import { cellStackSx, primaryTextSx, secondaryTextSx, tertiaryTextSx, awbTextSx } from "../orders/list2/cellStyles";
import { delayChip, routeCell, attemptsDonutData } from "./ctCells";
import AwbLinkCell from "../../common/AwbLinkCell";
import useAddressesByAwb from "../../common/hooks/useAddressesByAwb";
import { SHADOW } from "../../styles/foundation/effects";

const PICKUP_BUCKETS = [
  { key: "On Time", label: "On Time", color: "#0a9d72" },
  { key: "+1 Day", label: "+1 Day", color: "#c79000" },
  { key: "+2 Day", label: "+2 Day", color: "#e58200" },
  { key: "3+ Day", label: "3+ Day", color: "#d24b47" },
];

export default function PickupDelay({ pickUpDelayed }) {
  const [sizePerPage, setSizePerPage] = React.useState(20);
  const [page, setPage] = React.useState(0);
  const [orderItems, setOrderItems] = React.useState(null);
  const [openList, setOpenList] = React.useState(false);

  // Current page's rows — enrich only these AWBs with pickup/drop addresses for the grid.
  const pageData = _.orderBy(pickUpDelayed, "awbRegisteredDate", "desc").splice(page * sizePerPage, sizePerPage);
  const { addrMap } = useAddressesByAwb(pageData.map((r) => r.awbNumber));

  const delayedAgingChartData = _(pickUpDelayed)
    .groupBy(function (item) {
      if (item.pickupPendingAgeingDays >= 7) return "7+";
      return `${item.pickupPendingAgeingDays}`;
    })
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .orderBy("name", "asc")
    .value();

  const noOfAttemptsChart = _(pickUpDelayed)
    .groupBy(function (item) {
      return item.pickupFailedCount;
    })
    .map(function (items, key) {
      const n = key === "undefined" || key === "null" || key === "" ? 0 : Number(key);
      // Bar counts SHIPMENTS; the label is the number of pickup attempts.
      return { name: `${n} attempt${n === 1 ? "" : "s"}`, attempts: n, value: items.length };
    })
    .orderBy((r) => r.attempts, "asc")
    .value();

  const courierWiseSplit = _(pickUpDelayed)
    .groupBy("courierName")
    .map(function (items, key) {
      return {
        name: key.replaceAll('Forward', '').replaceAll('_', ' ').replaceAll('FORWARD', '').replaceAll('-', ''),
        value: items.length,
      };
    })
    .orderBy("value", "desc")
    .value();

  const paymentModeSplit = _(pickUpDelayed)
    .groupBy("paymentMode")
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .value();

  const pendingPickupGroupedByCourier = _(pickUpDelayed)
    .groupBy("courierName")
    .map(function (items, key) {
      const itemsGrouped = _(items).groupBy("pickupDelayLabel").value();
      let values = {};
      for (let k of Object.keys(itemsGrouped)) values[k] = itemsGrouped[k].length;
      return { name: key, values };
    })
    .orderBy((r) => _.sum(Object.values(r.values)), "desc")
    .value();

  const paymentDonut = paymentModeSplit.map((p) => ({ name: p.name, value: p.value, color: /cod/i.test(p.name) ? ERROR : SUCCESS }));
  const totalPay = _.sumBy(paymentModeSplit, "value") || 1;
  const codPerc = Math.round(((_.sumBy(paymentModeSplit.filter((p) => /cod/i.test(p.name)), "value")) / totalPay) * 100);

  const getItemList = (awb) => {
    const bodyData = { includes: "itemList", andfilter: [{ field: "awbNumber", operator: "eq", value: awb }] };
    post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, bodyData)
      .then((response) => {
        setOpenList(true);
        setOrderItems(response.data.data[0].item_list);
      })
      .catch((err) => {
        setOpenList(false);
        console.error(err);
      });
  };

  const handleClose = () => {
    setOrderItems(null);
    setOpenList(false);
  };

  return (
    <Grid container spacing={2}>
      <OrderItemsModal items={orderItems} setOpen={handleClose} openList={openList} />

      <Grid item xs={12}>
        <Panel title="Delayed Pickup Ageing" sub="How many days each shipment has been waiting for pickup — the curve traces the falloff so you see where most of your pending-pickup backlog sits.">
          <AreaCurve data={delayedAgingChartData} height={300} color="#e58200" xLabel="Days awaiting pickup" yLabel="Shipments" valueName="Shipments" />
        </Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="Pickup Attempts" sub="Share of shipments by how many pickup attempts the courier has made. Centre shows the total.">
          <Donut data={attemptsDonutData(noOfAttemptsChart)} centerValue={pickUpDelayed.length} centerLabel="Shipments" />
        </Panel>
      </Grid>

      <Grid item xs={12} md={6}>
        <Panel title="Payment Mode of Delayed Shipments">
          <Donut data={paymentDonut} centerValue={`${codPerc}%`} centerLabel="COD" />
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <Panel title="Courier Partners for Delayed Shipments">
          <Lollipop data={courierWiseSplit} nameLabel="Courier partner" valueLabel="Pending pickups" />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Panel title="Courier-wise Pickup Delay" sub="Split of each courier's pending pickups across the delay buckets.">
          <StackedComposition rows={pendingPickupGroupedByCourier} buckets={PICKUP_BUCKETS} />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: "10px", boxShadow: SHADOW.card, overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontFamily: FONT, fontSize: "11px", fontWeight: WEIGHT.extra, letterSpacing: "0.07em", textTransform: "uppercase", color: SUBTLE }}>Pickup Delays</Typography>
            <DelayMisDownloadButton
              rows={pickUpDelayed}
              filename="Pickup Delayed Shipments"
              baseColumns={[
                { label: "Reference", value: "shippingLabelId" },
                { label: "Courier Partner", value: "courierName" },
                { label: "AWB Number", value: "awbNumber" },
                { label: "Order Date", value: (row) => formatDate(row.awbRegisteredDate, "dd MMM, yyyy") },
                { label: "Zone", value: "merchantZone" },
                { label: "Delay", value: "pickupDelayText" },
                { label: "Pickup Attempt", value: "failedPickup" },
              ]}
            />
          </Box>
          <Box sx={{ px: 0 }}>
            <ProzoDataGrid
              columns={[
                {
                  field: "order", headerName: "Order Details", width: 190, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <Typography sx={primaryTextSx}>{row.reference || row.shippingLabelId || "—"}</Typography>
                      {row.shippingLabelId && row.shippingLabelId !== row.reference && <Typography sx={secondaryTextSx}>{row.shippingLabelId}</Typography>}
                      <Typography sx={secondaryTextSx}>{formatDate(row.awbRegisteredDate, "dd MMM, yyyy")}</Typography>
                      <Button sx={{ p: 0, minWidth: 0, justifyContent: "flex-start", fontSize: 11, textTransform: "none" }}
                        startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />} variant="link"
                        onClick={() => getItemList(row.awbNumber)}>View Items</Button>
                    </Box>
                  ),
                },
                {
                  field: "courier", headerName: "Courier Partner", width: 210, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <AwbLinkCell awb={row.awbNumber} sx={awbTextSx} />
                      <Typography sx={secondaryTextSx}>{addrMap[row.awbNumber]?.courierLabel || row.courierName}</Typography>
                    </Box>
                  ),
                },
                {
                  field: "route", headerName: "Pickup → Drop", width: 250, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => routeCell(row, addrMap),
                },
                {
                  field: "timeline", headerName: "Timeline", width: 210, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>Created: </Box>{formatDate(row.awbRegisteredDate, "dd MMM, hh:mm aa")}</Typography>
                      <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>Est Pickup: </Box>{row.estimatedPickupDate ? formatDate(row.estimatedPickupDate, "dd MMM, hh:mm aa") : "—"}</Typography>
                    </Box>
                  ),
                },
                {
                  field: "delay", headerName: "Delay", width: 140, headerAlign: "center", align: "center", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={{ ...cellStackSx, alignItems: "center" }}>
                      {delayChip(row.pickupDelayInDays)}
                      <Typography sx={tertiaryTextSx}>{row.failedPickup || 0} pickup attempts</Typography>
                    </Box>
                  ),
                },
                {
                  field: "invoice", headerName: "Invoice", width: 130, headerAlign: "right", align: "right", hideSortIcons: true,
                  renderCell: ({ row }) => <Typography sx={primaryTextSx}>{formatCurrency(Math.round(row.invoiceValue || 0))}</Typography>,
                },
              ]}
              autoHeight={true}
              rows={pageData}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              setPage={setPage}
              page={page}
              pagination={true}
              hideFooterPagination={false}
              filterMode={"server"}
              rowCount={pickUpDelayed.length}
              getRowId={(row) => row.id}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
